export type SecureCheckoutState = {
  checkoutSessionPublicId?: string;
  cancellationPolicy?: "non-refundable" | "flexible";
  paymentTiming?: "pay_now" | "pay_later";
  climateOffset?: boolean;
  workTrip?: boolean;
  businessName?: string;
  vatNumber?: string;
  renterIsDriver?: boolean | null;
  primaryDriverEmail?: string;
  dateFrom?: string;
  dateTo?: string;
  rentalType?: "driving" | "chauffeured" | "event";
  pickupTime?: string;
  dropoffTime?: string;
  hours?: number;
  insurancePackageId?: number;
  coverageConfirmation?: { context: string; packageId: number };
  coveragePromptedContext?: string;
  numPassengers?: number;
  deliveryAddress?: string;
  deliveryDistance?: number;
  airportCode?: string;
  message?: string;
  occasion?: string;
  quoteToken?: string;
  winBackToken?: string;
  probeToken?: string;
  coupon?: string;
};

const STORAGE_PREFIX = "bac:checkout-secure:";
const POST_AUTH_CLAIM_KEY = "bac:checkout-post-auth-claim";
const SENSITIVE_PARAMS = new Set([
  "delivery_address", "delivery_distance", "airport_code", "message", "occasion",
  "quote", "winback", "probe", "coupon",
]);
const SAFE_CHECKOUT_PARAMS = new Set([
  "car", "h",
]);
const memoryFallback = new Map<string, SecureCheckoutState>();
type StoredCheckoutState = SecureCheckoutState & { ownerId?: string };

function storageKey(flowId: string): string {
  return `${STORAGE_PREFIX}${flowId}`;
}

export function createCheckoutFlowId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  const hex = () => Math.floor(Math.random() * 0x10000).toString(16).padStart(4, "0");
  return `${hex()}${hex()}-${hex()}-4${hex().slice(1)}-${((8 + Math.floor(Math.random() * 4)).toString(16))}${hex().slice(1)}-${hex()}${hex()}${hex()}`;
}

export function normalizeCheckoutFlowId(candidate: string | null | undefined): string {
  const value = candidate?.trim() ?? "";
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value)
    ? value.toLowerCase()
    : createCheckoutFlowId();
}

export function rememberCheckoutFlowForPostAuthClaim(flowId: string): void {
  try {
    sessionStorage.setItem(POST_AUTH_CLAIM_KEY, flowId);
  } catch {
    // Callback-based auth cannot survive a page navigation without storage.
  }
}

export function canClaimCheckoutFlowAfterAuth(flowId: string): boolean {
  if (!flowId) return false;
  try {
    return sessionStorage.getItem(POST_AUTH_CLAIM_KEY) === flowId;
  } catch {
    return false;
  }
}

export function clearCheckoutFlowPostAuthClaim(flowId: string): void {
  try {
    if (sessionStorage.getItem(POST_AUTH_CLAIM_KEY) === flowId) {
      sessionStorage.removeItem(POST_AUTH_CLAIM_KEY);
    }
  } catch {
    // Best effort after the exact flow has been bound.
  }
}

export function readSecureCheckoutState(
  flowId: string,
  ownerId?: string,
  allowUnowned = ownerId === undefined,
): SecureCheckoutState {
  let stored: StoredCheckoutState | undefined;
  try {
    const raw = sessionStorage.getItem(storageKey(flowId));
    if (raw) stored = JSON.parse(raw) as StoredCheckoutState;
  } catch {
    // Private browsing and storage policy failures still retain the in-memory copy.
  }
  stored ??= memoryFallback.get(storageKey(flowId)) as StoredCheckoutState | undefined;
  if (!stored) return {};
  if (stored?.ownerId) {
    if (!ownerId) return {};
    if (stored.ownerId !== ownerId) {
      clearSecureCheckoutState(flowId);
      return {};
    }
  }
  if (!stored.ownerId && !allowUnowned) {
    clearSecureCheckoutState(flowId);
    return {};
  }
  const { ownerId: _ownerId, ...state } = stored;
  return state;
}

export function writeSecureCheckoutState(
  flowId: string,
  patch: SecureCheckoutState,
  ownerId?: string,
  allowUnownedClaim = false,
): SecureCheckoutState {
  // Reading an owned flow without a session is intentionally empty. Do not
  // turn that empty read into a destructive overwrite during reauthentication.
  if (!ownerId) {
    let stored = memoryFallback.get(storageKey(flowId)) as StoredCheckoutState | undefined;
    try {
      const raw = sessionStorage.getItem(storageKey(flowId));
      if (raw) stored = JSON.parse(raw) as StoredCheckoutState;
    } catch { /* use same-page copy */ }
    if (stored?.ownerId) return {};
  }
  const next: StoredCheckoutState = {
    ...readSecureCheckoutState(flowId, ownerId, !ownerId || allowUnownedClaim),
    ...patch,
  };
  if (ownerId) next.ownerId = ownerId;
  for (const field of Object.keys(next) as Array<keyof SecureCheckoutState>) {
    if (next[field] === undefined || next[field] === "") delete next[field];
  }
  memoryFallback.set(storageKey(flowId), next);
  try {
    sessionStorage.setItem(storageKey(flowId), JSON.stringify(next));
  } catch {
    // The same-page flow remains functional through memoryFallback.
  }
  return next;
}

export function consumeCheckoutParams(
  params: URLSearchParams,
  ownerId?: string,
  allowUnownedClaim = false,
): {
  flowId: string;
  state: SecureCheckoutState;
  safeSearch: URLSearchParams;
} {
  const flowId = normalizeCheckoutFlowId(params.get("h"));
  const existing = readSecureCheckoutState(flowId, ownerId, allowUnownedClaim);
  const numberValue = (name: string) => {
    const raw = params.get(name);
    if (!raw) return undefined;
    const value = Number(raw);
    return Number.isFinite(value) ? value : undefined;
  };
  const state = writeSecureCheckoutState(flowId, {
    ...existing,
    dateFrom: params.get("from") || existing.dateFrom,
    dateTo: params.get("to") || existing.dateTo,
    rentalType: (["driving", "chauffeured", "event"].includes(params.get("type") || "")
      ? params.get("type")
      : existing.rentalType) as SecureCheckoutState["rentalType"],
    pickupTime: params.get("pickupTime") || existing.pickupTime,
    dropoffTime: params.get("dropoffTime") || existing.dropoffTime,
    hours: numberValue("hours") ?? existing.hours,
    insurancePackageId: numberValue("insurancePackageId") ?? existing.insurancePackageId,
    numPassengers: numberValue("numPassengers") ?? existing.numPassengers,
    deliveryAddress: params.get("delivery_address")?.slice(0, 500) || existing.deliveryAddress,
    deliveryDistance: numberValue("delivery_distance") ?? existing.deliveryDistance,
    airportCode: params.get("airport_code") || existing.airportCode,
    message: params.get("message") ?? existing.message,
    occasion: params.get("occasion") || existing.occasion,
    quoteToken: params.get("quote") || existing.quoteToken,
    winBackToken: params.get("winback") || existing.winBackToken,
    probeToken: params.get("probe") || existing.probeToken,
    coupon: params.get("coupon") || existing.coupon,
  }, ownerId, allowUnownedClaim);
  const safeSearch = sanitizeCheckoutSearch(params);
  safeSearch.set("h", flowId);
  return { flowId, state, safeSearch };
}

export function sanitizeCheckoutSearch(params: URLSearchParams): URLSearchParams {
  const safe = new URLSearchParams();
  for (const [name, value] of params) {
    if (SAFE_CHECKOUT_PARAMS.has(name) && (name !== "chooseCoverage" || value === "1")) safe.append(name, value);
  }
  return safe;
}

export function sanitizeCheckoutUrl(raw: string): string {
  try {
    const base = typeof window !== "undefined" ? window.location.origin : "https://invalid.local";
    const url = new URL(raw, base);
    if (url.pathname.endsWith("/checkout")) {
      url.search = sanitizeCheckoutSearch(url.searchParams).toString();
    } else {
      for (const name of SENSITIVE_PARAMS) url.searchParams.delete(name);
    }
    return url.origin === "https://invalid.local"
      ? `${url.pathname}${url.search}${url.hash}`
      : url.toString();
  } catch {
    return raw.split("?")[0];
  }
}

export function clearSecureCheckoutState(flowId: string): void {
  memoryFallback.delete(storageKey(flowId));
  try {
    sessionStorage.removeItem(storageKey(flowId));
  } catch {
    // Best-effort cleanup after the booking reaches a terminal success.
  }
}